import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/forms/Field";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

function GoogleButton() {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button variant="outline" onClick={onClick} data-testid="google-signin-button" className="w-full h-12 rounded-full border-edge bg-white text-obsidian font-semibold hover:bg-white/90 gap-2">
      <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Continue with Google
    </Button>
  );
}

const shell = (children) => (
  <div className="min-h-screen bg-obsidian grain-bg flex flex-col items-center justify-center px-4 py-12">
    <Link to="/" className="mb-8"><Logo size={56} showText={false} /></Link>
    <div className="w-full max-w-md card-surface rounded-2xl p-7 sm:p-9">{children}</div>
  </div>
);

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(email, password);
      const dest = ["admin", "processor", "loan_officer"].includes(u.role) ? "/admin" : (location.state?.from || "/portal");
      navigate(dest);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };

  return shell(
    <>
      <h1 className="h-display text-3xl text-white">Portal Sign In</h1>
      <p className="mt-1 text-sm text-dim">Access your applications, documents, and messages.</p>
      <div className="mt-6"><GoogleButton /></div>
      <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-edge" /><span className="text-xs text-faint">OR</span><div className="flex-1 h-px bg-edge" /></div>
      <form onSubmit={submit} className="space-y-4">
        <TextField label="Email" name="email" type="email" value={email} onChange={(_, v) => setEmail(v)} testId="login-email" />
        <TextField label="Password" name="password" type="password" value={password} onChange={(_, v) => setPassword(v)} testId="login-password" />
        {err && <p className="text-sm text-brandred" data-testid="login-error">{err}</p>}
        <Button type="submit" disabled={busy} className="btn-gold w-full h-12 rounded-full gap-2" data-testid="login-submit">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4" /></>}
        </Button>
      </form>
      <div className="mt-5 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-dim hover:text-gold">Forgot password?</Link>
        <Link to="/register" className="text-gold hover:text-goldhi" data-testid="login-to-register">Create account</Link>
      </div>
    </>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (n, v) => setF((s) => ({ ...s, [n]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.email || !f.password) { setErr("Email and password are required"); return; }
    setBusy(true); setErr("");
    try {
      await register({ email: f.email, password: f.password, name: `${f.first || ""} ${f.last || ""}`.trim(), company: f.company || "", phone: f.phone || "", referral_code: localStorage.getItem("bullseye_ref") || "" });
      navigate("/portal");
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Registration failed"); }
    finally { setBusy(false); }
  };

  return shell(
    <>
      <h1 className="h-display text-3xl text-white">Create Account</h1>
      <p className="mt-1 text-sm text-dim">Track your deals and upload documents securely.</p>
      <div className="mt-6"><GoogleButton /></div>
      <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-edge" /><span className="text-xs text-faint">OR</span><div className="flex-1 h-px bg-edge" /></div>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <TextField label="First Name" name="first" value={f.first} onChange={set} testId="register-first" />
          <TextField label="Last Name" name="last" value={f.last} onChange={set} testId="register-last" />
        </div>
        <TextField label="Email" name="email" type="email" value={f.email} onChange={set} testId="register-email" />
        <TextField label="Phone" name="phone" value={f.phone} onChange={set} testId="register-phone" />
        <TextField label="Password" name="password" type="password" value={f.password} onChange={set} testId="register-password" />
        {err && <p className="text-sm text-brandred" data-testid="register-error">{err}</p>}
        <Button type="submit" disabled={busy} className="btn-gold w-full h-12 rounded-full gap-2" data-testid="register-submit">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Create Account <ArrowRight className="w-4 h-4" /></>}
        </Button>
      </form>
      <div className="mt-5 text-sm text-center"><span className="text-dim">Already have an account? </span><Link to="/login" className="text-gold" data-testid="register-to-login">Sign in</Link></div>
    </>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault(); setBusy(true);
    try { const { default: api } = await import("@/lib/api"); await api.post("/auth/forgot-password", { email }); setSent(true); } catch {} finally { setBusy(false); }
  };
  return shell(
    <>
      <h1 className="h-display text-3xl text-white">Reset Password</h1>
      {sent ? (
        <p className="mt-4 text-dim text-sm">If an account exists for {email}, a reset link has been generated. Contact us if you need assistance.</p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <TextField label="Email" name="email" type="email" value={email} onChange={(_, v) => setEmail(v)} testId="forgot-email" />
          <Button type="submit" disabled={busy} className="btn-gold w-full h-12 rounded-full" data-testid="forgot-submit">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}</Button>
        </form>
      )}
      <div className="mt-5 text-sm text-center"><Link to="/login" className="text-gold">Back to sign in</Link></div>
    </>
  );
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    import("@/lib/api").then(({ default: api }) => api.get(`/auth/invite/${encodeURIComponent(token)}`))
      .then((r) => setInfo(r.data)).catch((e) => setInfo({ error: formatApiError(e.response?.data?.detail) }));
  }, [token]);
  const submit = async (e) => {
    e?.preventDefault(); setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { default: api } = await import("@/lib/api");
      const { data } = await api.post("/auth/accept-invite", { token, password: pw });
      window.location.assign(["admin", "processor", "loan_officer"].includes(data.role) ? "/admin" : "/portal");
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); setBusy(false); }
  };
  if (!info) return shell(<Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />);
  if (info.error) return shell(<>
    <h1 className="h-display text-3xl text-white">Invite unavailable</h1>
    <p className="mt-3 text-sm text-dim" data-testid="invite-error">{info.error}</p>
    <div className="mt-5 text-sm"><Link to="/login" className="text-gold">Go to sign in</Link></div>
  </>);
  return shell(
    <>
      <h1 className="h-display text-3xl text-white">Welcome{info.name ? `, ${info.name.split(" ")[0]}` : ""}</h1>
      <p className="mt-1 text-sm text-dim" data-testid="invite-email">Set a password for <span className="text-gold">{info.email}</span>.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <TextField label="Password" name="password" type="password" value={pw} onChange={(_, v) => setPw(v)} testId="invite-password" />
        <TextField label="Confirm Password" name="password2" type="password" value={pw2} onChange={(_, v) => setPw2(v)} testId="invite-password-confirm" />
        {err && <p className="text-sm text-brandred" data-testid="invite-submit-error">{err}</p>}
        <Button type="submit" disabled={busy} className="btn-gold w-full h-12 rounded-full" data-testid="invite-submit">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Set Password & Sign In"}</Button>
      </form>
    </>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const token = params.get("token");
  const submit = async (e) => {
    e?.preventDefault(); setBusy(true); setErr("");
    try { const { default: api } = await import("@/lib/api"); await api.post("/auth/reset-password", { token, password }); navigate("/login"); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); } finally { setBusy(false); }
  };
  return shell(
    <>
      <h1 className="h-display text-3xl text-white">Set New Password</h1>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <TextField label="New Password" name="password" type="password" value={password} onChange={(_, v) => setPassword(v)} testId="reset-password" />
        {err && <p className="text-sm text-brandred">{err}</p>}
        <Button type="submit" disabled={busy} className="btn-gold w-full h-12 rounded-full" data-testid="reset-submit">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Password"}</Button>
      </form>
    </>
  );
}
